import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContactsIcon from "@mui/icons-material/Contacts";
import BusinessIcon from "@mui/icons-material/Business";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import { getContactThunk, getOrganizationsThunk } from "../../contact/contactThunk";
import { useDispatch } from "react-redux";
import { QR, QrActionButton } from "../../qrCode/components/qrUi";

// Header card for the contact pages (same look as the other modules).
// Optional: `stats` (cards inside the header) and `children` (page content inside the card).
export default function PageButtons({ stats = [], children, onCreate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [activePath, setActivePath] = useState(location.pathname);
  // eslint-disable-next-line no-unused-vars
  const [expanded, setExpanded] = useState("Contact"); // default UI state

  const sections = [
    // { label: "Organization", routes: APP_ROUTES.ORGANIZATION, icon: <AccountTreeIcon /> },
    { label: "Contact", routes: APP_ROUTES.CONTACT, icon: <BusinessIcon /> }];

  const knownPaths = new Set(
    sections.flatMap((s) => [s.routes.LIST, s.routes.CREATION])
  );

  useEffect(() => {
    if (!knownPaths.has(location.pathname)) {
      navigate(APP_ROUTES.ORGANIZATION.LIST, { replace: true });
      setActivePath(APP_ROUTES.ORGANIZATION.LIST);
      setExpanded("Organization");
    } else {
      setActivePath(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActivePath(location.pathname);
    const match = sections.find(
      (s) =>
        location.pathname === s.routes.LIST ||
        location.pathname === s.routes.CREATION
    );
    if (match) setExpanded(match.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClick = (path) => navigate(path);

  useEffect(() => {
    dispatch(getContactThunk());
    dispatch(getOrganizationsThunk());

  }, [dispatch]);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: 1.5, sm: 2, md: 2.5 },
        borderRadius: 3,
        border: `1px solid ${QR.border}`,
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
      }}
    >
      {/* Title + one context button */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              borderRadius: 2.5,
              color: "#fff",
              background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
              boxShadow: `0 8px 18px -8px ${QR.main}`,
            }}
          >
            <ContactsIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
              Contacts
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
              Everyone you talk to on WhatsApp, with their branch, tags and organization
            </Typography>
          </Box>
        </Stack>

        {/* List page → "Add Contact"; create page → "Back to List" */}
        <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
          {activePath === APP_ROUTES.CONTACT.CREATION ? (
            <QrActionButton
              variant="solid"
              color="#fc8006" // logo orange
              gradientTo="#fb923c"
              icon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
              label="Back to List"
              onClick={() => handleClick(APP_ROUTES.CONTACT.LIST)}
            />
          ) : (
            <QrActionButton
              variant="solid"
              color={QR.blue}
              gradientTo="#3b82f6"
              icon={<PersonAddAlt1Icon sx={{ fontSize: 17 }} />}
              label="Add Contact"
              // list page: open the add-contact popup; otherwise go to the create page
              onClick={() => (onCreate ? onCreate() : handleClick(APP_ROUTES.CONTACT.CREATION))}
            />
          )}
        </Box>
      </Box>

      {/* Stats cards */}
      {stats.length > 0 && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${QR.border}`,
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${stats.length}, minmax(0, 1fr))` },
            gap: 1.5,
          }}
        >
          {stats.map((s, idx) => (
            <Stack
              key={s.label}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", md: "auto" },
                minWidth: 0,
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 3,
                bgcolor: "#fff",
                border: `1px solid ${QR.border}`,
                borderTop: `3px solid ${s.color}`,
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
              }}
            >
              <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                {s.icon}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                  {s.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                  {s.value}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {/* Page content inside the same card */}
      {children && (
        <Box sx={{ mt: 2.5, pt: 0.5, borderTop: `1px solid ${QR.border}` }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
